import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiDownload, FiCopy, FiCheck, FiUser, FiMessageCircle } from 'react-icons/fi';
import { useLanguage } from '../../contexts/LanguageContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Container,
  Header,
  Title,
  ResultsList,
  ResultItem,
  DateTimeColumn,
  Date as DateDisplay,
  Time,
  ClientColumn,
  ClientTitle,
  ClientText,
  OperatorRow,
  OperatorIcon,
  OperatorLabel,
  OperatorName,
  ContentColumn,
  ActionButtons,
  ViewButton,
  DownloadButton,
  CopyButton,
  ChatButton,
  Modal,
  ModalContent,
  ModalButtons,
  CancelButton,
  ConfirmButton,
  ResultDetails,
  ResultDetailRow,
  ResultDetailLabel,
  ResultDetailValue,
  Toast
} from './styles';
import Loading from '../../components/Loading';
import Tooltip from '../../components/tooltip';
import { resultService, ResultData } from '../../services/results';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from 'styled-components';

// Necessário para o TypeScript reconhecer o autotable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable: any;
    previousAutoTable: any;
  }
}

interface Result {
  _id: string;
  id?: string;
  date: string;
  time: string;
  operator?: {
    name: string;
    email?: string;
  };
  content: string;
  conversationId?: string;
}

interface ResultsProps {
  isAdmin?: boolean;
}

// Interface para representar a estrutura dos dados salvos
interface StructuredResultData {
  personalInfo?: {
    fullName?: string;
    birthDate?: string;
    nationality?: string;
    cpf?: string;
    rg?: string;
  };
  education?: {
    hasCompletedCollege?: string;
    institution?: string;
    degree?: string;
    fieldsOfStudy?: string;
    datesOfCompletion?: string;
    relevantContinuingEducation?: string;
  };
  careerHistory?: {
    jobTitles?: string;
    employers?: string;
    durationInEachRole?: string;
    descriptionOfResponsibilities?: string;
    anySignificantCareer?: string;
  };
  sourceOfWealth?: {
    professionalIncome?: string;
    ownsBusinessOwnership?: string;
    isEntrepreneur?: string;
    sectorActivity?: string;
    operateFinancial?: string;
    traderType?: string;
    hasInvestmentIncome?: string;
    investmentTypes?: string;
    hasInheritedAssets?: string;
    inheritanceDetails?: string;
    soldCompanyProperty?: string;
    saleDetails?: string;
    hasGovernmentContracts?: string;
    governmentContractDetails?: string;
    worksWithAgrobusiness?: string;
    agrobusinessDetails?: string;
  };
  financialInfo?: {
    totalNetWorth?: string;
    liquidNetWorth?: string;
    expectedAccountSize?: string;
    otherWealthSources?: string;
  };
  pepStatus?: {
    holdsPoliticalOffice?: string;
    politicalOfficeDetails?: string;
    hasRelationshipWithPEP?: string;
    pepRelationshipDetails?: string;
    hasGovernmentPosition?: string;
    governmentPositionDetails?: string;
  };
  completeContent?: string;
}

const Results: React.FC<ResultsProps> = ({ isAdmin = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [results, setResults] = useState<Result[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const fetchRef = useRef(false);
  const [selectedResult, setSelectedResult] = useState<Result | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const { user, hasAdminAccess, isManager, getUserOrganizationId } = useAuth();

  // Formatador de datas
  const formatDate = (dateStr: string) => {
    // @ts-ignore - Ignora erro de tipagem do Date
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
  };

  // Extrair a hora de uma data ISO
  const extractTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // Converter o formato da API para o formato usado no componente
  const convertApiResult = (apiResult: ResultData): Result => {
    // Verificar se o conteúdo do resultado é um objeto e convertê-lo em string, se necessário
    let contentString = "No content available";
    
    try {
      if (apiResult.result === undefined || apiResult.result === null) {
        contentString = "No content available";
      } else if (typeof apiResult.result === 'string') {
        // Tentar analisar se a string é um JSON
        try {
          const jsonObject = JSON.parse(apiResult.result);
          
          // Se for um objeto com completeContent, use-o, caso contrário formate a estrutura
          if (jsonObject.completeContent) {
            contentString = jsonObject.completeContent;
          } else if (jsonObject.personalInfo) {
            // Construir uma string formatada com os dados estruturados
            contentString = '';
            
            // Informações pessoais
            if (jsonObject.personalInfo) {
              contentString += '--- PERSONAL INFORMATION ---\n';
              if (jsonObject.personalInfo.fullName) contentString += `Full Name: ${jsonObject.personalInfo.fullName}\n`;
              if (jsonObject.personalInfo.birthDate) contentString += `Birth Date: ${jsonObject.personalInfo.birthDate}\n`;
              if (jsonObject.personalInfo.nationality) contentString += `Nationality: ${jsonObject.personalInfo.nationality}\n`;
              contentString += '\n';
            }
            
            // Source of Wealth
            if (jsonObject.sourceOfWealth) {
              contentString += '--- SOURCE OF WEALTH ---\n';
              if (jsonObject.sourceOfWealth.isEntrepreneur) contentString += `Entrepreneur: ${jsonObject.sourceOfWealth.isEntrepreneur}\n`;
              if (jsonObject.sourceOfWealth.sectorActivity) contentString += `Sector Activity: ${jsonObject.sourceOfWealth.sectorActivity}\n`;
              if (jsonObject.sourceOfWealth.operateFinancial) contentString += `Operates in Financial Market: ${jsonObject.sourceOfWealth.operateFinancial}\n`;
              if (jsonObject.sourceOfWealth.hasInvestmentIncome) contentString += `Has Investment Income: ${jsonObject.sourceOfWealth.hasInvestmentIncome}\n`;
              contentString += '\n';
            }
            
            // Outros dados importantes
            if (jsonObject.financialInfo) {
              contentString += '--- FINANCIAL INFORMATION ---\n';
              if (jsonObject.financialInfo.totalNetWorth) contentString += `Total Net Worth: ${jsonObject.financialInfo.totalNetWorth}\n`;
              contentString += '\n';
            }
          } else {
            // Se não tiver estrutura reconhecida, mostrar o JSON formatado
            contentString = JSON.stringify(jsonObject, null, 2);
          }
        } catch (e) {
          // Se não for um JSON válido, use a string como está
          contentString = apiResult.result;
        }
      } else if (typeof apiResult.result === 'object') {
        // Verificar se há uma propriedade completeContent
        if (apiResult.result) {
          // Ver se o objeto apiResult.result é o objeto completo ou se está dentro de outra propriedade result
          let resultObj: any = apiResult.result;
          
          // Se o objeto apiResult.result.result existe, use-o 
          if (resultObj.result && typeof resultObj.result === 'object') {
            resultObj = resultObj.result;
          }
          
          // Verificar se tem completeContent
          if ('completeContent' in resultObj) {
            contentString = resultObj.completeContent || '';
          } 
          // Verificar se tem propriedades de informações pessoais
          else if (resultObj.personalInfo) {
            // Construir uma string formatada com os dados estruturados
            contentString = '';
            
            // Informações pessoais
            if (resultObj.personalInfo) {
              contentString += '--- PERSONAL INFORMATION ---\n';
              if (resultObj.personalInfo.fullName) contentString += `Full Name: ${resultObj.personalInfo.fullName}\n`;
              if (resultObj.personalInfo.birthDate) contentString += `Birth Date: ${resultObj.personalInfo.birthDate}\n`;
              if (resultObj.personalInfo.nationality) contentString += `Nationality: ${resultObj.personalInfo.nationality}\n`;
              contentString += '\n';
            }
            
            // Source of Wealth (o que você está tendo problemas para visualizar)
            if (resultObj.sourceOfWealth) {
              contentString += '--- SOURCE OF WEALTH ---\n';
              if (resultObj.sourceOfWealth.isEntrepreneur) contentString += `Entrepreneur: ${resultObj.sourceOfWealth.isEntrepreneur}\n`;
              if (resultObj.sourceOfWealth.sectorActivity) contentString += `Sector Activity: ${resultObj.sourceOfWealth.sectorActivity}\n`;
              if (resultObj.sourceOfWealth.operateFinancial) contentString += `Operates in Financial Market: ${resultObj.sourceOfWealth.operateFinancial}\n`;
              if (resultObj.sourceOfWealth.hasInvestmentIncome) contentString += `Has Investment Income: ${resultObj.sourceOfWealth.hasInvestmentIncome}\n`;
              contentString += '\n';
            }
            
            // Outros dados importantes
            if (resultObj.financialInfo) {
              contentString += '--- FINANCIAL INFORMATION ---\n';
              if (resultObj.financialInfo.totalNetWorth) contentString += `Total Net Worth: ${resultObj.financialInfo.totalNetWorth}\n`;
              contentString += '\n';
            }
          } else {
            // Tentar converter o objeto para JSON
            contentString = JSON.stringify(resultObj, null, 2);
          }
        } else {
          // Tentar converter o objeto para JSON
          contentString = JSON.stringify(apiResult.result, null, 2);
        }
      } else {
        // Para qualquer outro tipo, converter para string
        contentString = String(apiResult.result);
      }
    } catch (error) {
      // Garantir que nunca retornamos undefined mesmo em caso de erro
      console.error("Error converting result content:", error);
      contentString = "Error processing content";
    }
    
    // Extrair conversationId
    let conversationId = apiResult.conversationId || '';
    
    // Se não houver conversationId direto, tentar extrair de additionalData
    if (!conversationId && apiResult.additionalData) {
      // Verifique se additionalData é uma string contendo JSON
      if (typeof apiResult.additionalData === 'string') {
        try {
          const additionalData = JSON.parse(apiResult.additionalData);
          conversationId = additionalData.conversationId || '';
        } catch (e) {
          // Ignorar erro de parsing
        }
      } 
      // Ou se additionalData já é um objeto
      else if (typeof apiResult.additionalData === 'object' && apiResult.additionalData !== null) {
        conversationId = apiResult.additionalData.conversationId || '';
      }
    }
    
    const result: Result = {
      _id: apiResult._id || apiResult.id || '',
      date: apiResult.createdAt || new Date().toISOString(),
      time: extractTime(apiResult.createdAt || new Date().toISOString()),
      content: contentString,
      conversationId: conversationId
    };

    // Adicionar informações do operador se existirem
    if (apiResult.userId && typeof apiResult.userId !== 'string') {
      result.operator = {
        name: apiResult.userId.name || 'Unknown',
        email: apiResult.userId.email || ''
      };
    }

    return result;
  };

  useEffect(() => {
    const fetchResults = async () => {
      if (fetchRef.current) return;
      fetchRef.current = true;

      setIsLoading(true);
      try {
        // A lógica de filtros foi movida para o service
        const apiResults = await resultService.getResults();
        
        // Verificar se apiResults existe e é um array
        if (!apiResults || !Array.isArray(apiResults)) {
          setResults([]);
          setIsLoading(false);
          return;
        }
        
        // Converter os resultados para o formato usado no componente
        const formattedResults = apiResults.map(convertApiResult);
        
        // Ordenar os resultados em ordem decrescente de data
        formattedResults.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        setResults(formattedResults);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading results:', error);
        setResults([]);
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [user, getUserOrganizationId]);

  const handleView = (result: Result) => {
    setSelectedResult(result);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedResult(null);
  };

  const handleDownload = (result: Result) => {
    try {
      // Criar documento PDF
      const doc = new jsPDF();
      
      // Calcular posição vertical atual
      let yPos = 20;
      
      // Adicionar o título
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Relatório de Resultado", 105, yPos, { align: "center" });
      yPos += 20; // Avançar posição vertical
      
      // Adicionar informações do cliente
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Informações:", 20, yPos);
      yPos += 10; // Avançar posição vertical
      
      // Preparar dados para tabela
      const rows = [];
      
      // Adicionar informação do operador (se administrador)
      if (isAdmin && result.operator) {
        rows.push(["Operador:", result.operator.name]);
      }
      
      const formattedDate = formatDate(result.date);
      rows.push(["Data:", `${formattedDate} às ${result.time}`]);
      
      // Desenhar tabela manualmente
      doc.setFontSize(10);
      rows.forEach((row, index) => {
        doc.setFont("helvetica", "bold");
        doc.text(row[0], 20, yPos + (index * 7));
        doc.setFont("helvetica", "normal");
        doc.text(row[1], 50, yPos + (index * 7));
      });
      
      // Atualizar posição após a tabela
      yPos += (rows.length * 7) + 15;
      
      // Adicionar conteúdo
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Conteúdo:", 20, yPos);
      yPos += 8;
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      
      // Quebrar o texto em linhas para evitar corte
      const textWidth = 170;
      const contentLines = doc.splitTextToSize(result.content, textWidth);
      doc.text(contentLines, 20, yPos);
      
      // Adicionar rodapé
      const pageHeight = doc.internal.pageSize.getHeight ? 
        doc.internal.pageSize.getHeight() : 
        doc.internal.pageSize.height;
      
      // Obter data e hora atual formatadas
      const currentDate = new Date();
      const formattedDateTime = `${currentDate.toLocaleDateString('pt-BR')} às ${currentDate.getHours().toString().padStart(2, '0')}:${currentDate.getMinutes().toString().padStart(2, '0')}`;
      
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Report generated on ${formattedDateTime}`, 
        105, 
        pageHeight - 10, 
        { align: "center" }
      );
      
      // Salvar o PDF
      doc.save(`resultado_${result._id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF. Please try again.');
    }
  };

  const handleCopy = (result: Result) => {
    // Copiar o conteúdo para a área de transferência
    navigator.clipboard.writeText(result.content)
      .then(() => {
        setShowCopyToast(true);
        setTimeout(() => {
          setShowCopyToast(false);
        }, 3000);
      })
      .catch(err => {
        console.error('Failed to copy content:', err);
      });
  };

  const handleChatNavigate = (result: Result) => {
    if (result.conversationId) {
      setIsChatLoading(true);
      // Simular um pequeno delay para mostrar o loading antes da navegação
      setTimeout(() => {
        navigate(`/compliance/pending?conversationId=${result.conversationId}&full=true`);
        setIsChatLoading(false);
      }, 500);
    } else {
      alert('Não foi possível encontrar a conversa associada a este resultado.');
    }
  };

  return (
    <Container>
      <Header>
        <Title>{t('results.title')}</Title>
      </Header>

      {(isLoading || isChatLoading) ? (
        <Loading />
      ) : (
        <ResultsList>
          {results.length > 0 ? (
            results.map(result => (
              <React.Fragment key={result._id}>
                {isAdmin && result.operator && (
                  <OperatorRow>
                    <OperatorIcon>
                      {FiUser({ size: 20 })}
                    </OperatorIcon>
                    <OperatorLabel>{t('results.operator')}:</OperatorLabel>
                    <OperatorName>{result.operator.name}</OperatorName>
                  </OperatorRow>
                )}
                
                <ResultItem>
                  <DateTimeColumn>
                    <DateDisplay>{formatDate(result.date)}</DateDisplay>
                    <Time>{result.time}</Time>
                  </DateTimeColumn>
                  
                  <ClientColumn>
                    <ClientTitle>{t('results.content')}</ClientTitle>
                    <ClientText>{result.content && typeof result.content === 'string' 
                      ? (result.content.substring(0, 30) + '...') 
                      : t('results.noContent')}</ClientText>
                  </ClientColumn>
                  
                  <ContentColumn>
                    {result.content}
                  </ContentColumn>
                  
                  <ActionButtons>
                    <Tooltip text={t('common.view')}>
                      <ViewButton onClick={() => handleView(result)}>
                        {FiEye({ size: 20 })}
                      </ViewButton>
                    </Tooltip>
                    
                    <Tooltip text={t('results.download')}>
                      <DownloadButton onClick={() => handleDownload(result)}>
                        {FiDownload({ size: 20 })}
                      </DownloadButton>
                    </Tooltip>
                    
                    <Tooltip text={t('results.copy')}>
                      <CopyButton onClick={() => handleCopy(result)}>
                        {FiCopy({ size: 20 })}
                      </CopyButton>
                    </Tooltip>
                    
                    <Tooltip text={t('results.seeConversation')}>
                      <ChatButton onClick={() => handleChatNavigate(result)}>
                        {FiMessageCircle({ size: 20 })}
                      </ChatButton>
                    </Tooltip>
                    
                  </ActionButtons>
                </ResultItem>
              </React.Fragment>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', width: '100%' }}>
              <p>{t('results.noResults')}</p>
            </div>
          )}
        </ResultsList>
      )}

      {/* Modal de visualização do resultado */}
      {viewModalOpen && selectedResult && (
        <Modal>
          <ModalContent>
            <h3>{t('results.details')}</h3>
            
            <ResultDetails>
              <ResultDetailRow>
                <ResultDetailLabel>{t('results.date')}:</ResultDetailLabel>
                <ResultDetailValue>{formatDate(selectedResult.date)} às {selectedResult.time}</ResultDetailValue>
              </ResultDetailRow>
              
              {isAdmin && selectedResult.operator && (
                <ResultDetailRow>
                  <ResultDetailLabel>{t('results.operator')}:</ResultDetailLabel>
                  <ResultDetailValue>{selectedResult.operator.name}</ResultDetailValue>
                </ResultDetailRow>
              )}
              
              <ResultDetailRow>
                <ResultDetailLabel>{t('results.content')}:</ResultDetailLabel>
                <ResultDetailValue>{selectedResult.content}</ResultDetailValue>
              </ResultDetailRow>
            </ResultDetails>
            
            <ModalButtons>
              <CancelButton onClick={handleCloseViewModal}>
                {t('common.close')}
              </CancelButton>
              <ConfirmButton onClick={() => handleCopy(selectedResult)}>
                {t('results.copyContent')}
              </ConfirmButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {/* Toast de confirmação de cópia */}
      {showCopyToast && (
        <Toast>
          {FiCheck({ size: 16, color: theme.primaryOn })}
          <span>{t('results.copySuccess')}</span>
        </Toast>
      )}
    </Container>
  );
};

export default Results; 